import React, { useMemo, useState, useEffect } from "react";
import { FixationSaccadeSeries } from "model/EyeMovement";

interface DyslexiaEyeMovementCanvasProps {
  series?: FixationSaccadeSeries | null;
  background?: string;
  excessiveThreshold?: number;
  className?: string;
}

const FIXATION_RADIUS_SCALE = 4.5;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const DyslexiaEyeMovementCanvas: React.FC<DyslexiaEyeMovementCanvasProps> = ({
  series,
  background,
  excessiveThreshold = 0.3,
  className,
}) => {
  const [backgroundDataUrl, setBackgroundDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!background) {
      setBackgroundDataUrl(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL("image/png");
          setBackgroundDataUrl(dataUrl);
        }
      } catch (error) {
        console.error("Failed to convert image to data URL:", error);
        setBackgroundDataUrl(background);
      }
    };

    img.onerror = () => {
      setBackgroundDataUrl(background);
    };

    img.src = background;
  }, [background]);

  const fixations = useMemo(() => {
    if (!series?.fixations.length) {
      return [];
    }

    return series.fixations.map((fixation) => {
      const radius = clamp(Math.sqrt(fixation.duration) * FIXATION_RADIUS_SCALE, 0.6, 6);
      return {
        fixation,
        x: clamp(fixation.position.xPercentage, 0, 100),
        y: clamp(fixation.position.yPercentage, 0, 100),
        radius,
        isExcessive: fixation.duration > excessiveThreshold,
      };
    });
  }, [series, excessiveThreshold]);

  const saccades = useMemo(() => {
    if (!series?.saccades.length) {
      return [];
    }

    return series.saccades.map((saccade, index) => ({
      index,
      x1: clamp(saccade.from.position.xPercentage, 0, 100),
      y1: clamp(saccade.from.position.yPercentage, 0, 100),
      x2: clamp(saccade.to.position.xPercentage, 0, 100),
      y2: clamp(saccade.to.position.yPercentage, 0, 100),
    }));
  }, [series]);

  return (
    <div
      className={className}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
        }}
      >
        <rect x={0} y={0} width={100} height={100} fill="#ffffff" />
        {backgroundDataUrl ? (
          <image
            href={backgroundDataUrl}
            x={0}
            y={0}
            width={100}
            height={100}
            preserveAspectRatio="xMidYMid meet"
          />
        ) : null}
        {saccades.map((saccade) => (
          <line
            key={`saccade-${saccade.index}`}
            x1={saccade.x1}
            y1={saccade.y1}
            x2={saccade.x2}
            y2={saccade.y2}
            stroke="rgba(33, 37, 41, 0.45)"
            strokeWidth={0.4}
            strokeLinecap="round"
          />
        ))}
        {fixations.map(({ fixation, x, y, radius, isExcessive }) => (
          <circle
            key={`fixation-${fixation.index}`}
            cx={x}
            cy={y}
            r={radius}
            fill={isExcessive ? "rgba(220, 53, 69, 0.45)" : "rgba(25, 135, 84, 0.45)"}
            stroke={isExcessive ? "rgba(220, 53, 69, 0.85)" : "rgba(25, 135, 84, 0.85)"}
            strokeWidth={0.5}
          >
            <title>{`Fixation ${fixation.index + 1}: ${fixation.duration.toFixed(2)}s`}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
};

export default DyslexiaEyeMovementCanvas;
